import React, { useState, useEffect, useRef, useCallback } from "react";
import { useWorker } from "../context/WorkerContext";
import { toast } from "react-toastify";
import { X, ChevronDown, Check, Search, ArrowRight, User, Users } from "lucide-react";
import worker from "../assets/worker.png";

// ─────────────────────────────────────────────────────────────────────────────
// Shared sub-components — defined OUTSIDE to prevent remount on every render
// ─────────────────────────────────────────────────────────────────────────────

const RadioOption = ({ selected }) => (
  <span
    className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0
      ${selected ? "border-indigo-600 bg-indigo-600" : "border-gray-300"}`}
  >
    {selected && <span className="w-1.5 h-1.5 rounded-full bg-white block" />}
  </span>
);

const StepDot = ({ stepNum, label, icon: Icon, currentStep }) => {
  const done = currentStep > stepNum;
  const active = currentStep === stepNum;
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div
        className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300
          ${done ? "bg-green-500" : active ? "bg-indigo-600" : "bg-gray-200"}`}
      >
        {done ? (
          <Check className="w-3 h-3 text-white" />
        ) : (
          <Icon className={`w-3 h-3 ${active ? "text-white" : "text-gray-400"}`} />
        )}
      </div>
      <span
        className={`text-[10px] font-semibold ${
          active ? "text-indigo-600" : done ? "text-green-600" : "text-gray-400"
        }`}
      >
        {label}
      </span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Referral code validation — alphanumeric, exactly 4 or 6 characters
// ─────────────────────────────────────────────────────────────────────────────
const isValidReferralCode = (code) => /^[a-zA-Z0-9]{4}$|^[a-zA-Z0-9]{6}$/.test(code);

// ─────────────────────────────────────────────────────────────────────────────

const WorkerDetailModal = ({
  isOpen,
  onClose,
  initialData = null,
  editType = null,
}) => {
  const { token, user, setUser, verifyToken } = useWorker();

  // ── Step ──────────────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);

  // ── Step 1 state ──────────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedlocationobject, setselectedlocationobject] = useState(null);

  // ── Step 2 state ──────────────────────────────────────────────────────────
  const [selectedRelativeType, setSelectedRelativeType] = useState("");
  const [relativename, setRelativename] = useState("");
  const [familycontactno, setfamilycontactno] = useState("");
  const [lastcompany, setlastcompany] = useState("");
  const [presentaddress, setpresentaddress] = useState("");
  const [education, seteducation] = useState("");
  const [educationSearch, setEducationSearch] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [isReferralPrefilled, setIsReferralPrefilled] = useState(false);

  // ── Dropdown open states ──────────────────────────────────────────────────
  const [isGenderOpen, setIsGenderOpen] = useState(false);
  const [isSkillsOpen, setIsSkillsOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isRelativeTypeOpen, setIsRelativeTypeOpen] = useState(false);
  const [isEducationOpen, setIsEducationOpen] = useState(false);

  // ── Data ──────────────────────────────────────────────────────────────────
  const [skills, setSkills] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [phoneNumberError, setPhoneNumberError] = useState("");

  // ── Refs ──────────────────────────────────────────────────────────────────
  const genderRef = useRef(null);
  const skillsRef = useRef(null);
  const locationRef = useRef(null);
  const relativeTypeRef = useRef(null);
  const educationRef = useRef(null);

  const educationOptions = [
    "No Formal Education",
    "10th",
    "12th",
    "Graduation",
    "P.G. (Postgraduates)",
    "Diploma",
    "ITI",
    "Other",
  ];

  const filteredEducation = educationOptions.filter((opt) =>
    opt.toLowerCase().includes(educationSearch.toLowerCase())
  );

  // ── Check if user already has data saved ─────────────────────────────────
  const isExistingUser = !!(
    editType ||
    initialData?.preferredLocationId ||
    initialData?.skillIds?.length ||
    user?.preferredLocationId ||
    user?.skillIds?.length
  );

  const handleFamilyContactChange = (e) => {
  const val = e.target.value.replace(/\D/g, "");
  if (val.length <= 10) {
    setfamilycontactno(val);
    
    // Compare with user's phone from the verify-token response
    if (val.length === 10 && val === user?.phone) {
      setPhoneNumberError("We do not allow adding your own phone number as a relative's contact");
    } else {
      setPhoneNumberError("");
    }
  }
};

  // ── Fetch skills ──────────────────────────────────────────────────────────
  const fetchSkills = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/lenskart/skills`);
      const data = await res.json();
      if (data.success && Array.isArray(data.skills)) {
        const active = data.skills.filter((s) => s.isActive);
        setSkills(active);
        const savedIds = user?.skillIds || [];
        if (savedIds.length) {
          setSelectedSkills(active.filter((s) => savedIds.includes(s._id)).slice(0, 5));
        } else {
          setSelectedSkills([]);
        }
      }
    } catch {
      toast.error("Failed to load skills");
    } finally {
      setIsLoading(false);
    }
  }, [user?.skillIds]);

  // ── Fetch locations ───────────────────────────────────────────────────────
const fetchLocations = useCallback(async () => {
  setIsLoadingLocations(true);
  try {
    const res = await fetch(`${process.env.REACT_APP_BASE_URL}/lenskart/locations`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await res.json();
    if (data.success && Array.isArray(data.locations)) {
      const active = data.locations.filter((loc) => loc.isActive);
      setLocations(active);
      if (initialData?.preferredLocationId) {
        const matched = active.find((loc) => loc._id === initialData.preferredLocationId);
        if (matched) {
          setSelectedLocation(matched.name);
          setselectedlocationobject(matched);
        }
      }
    }
  } catch {
    toast.error("Failed to load locations");
  } finally {
    setIsLoadingLocations(false);
  }
}, [initialData?.preferredLocationId, token]);

  // ── Prefill on open ───────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setName(initialData?.name || "");
      setSelectedSkills([]);
      setSelectedLocation(initialData?.preferredLocation || "");
      setSelectedGender(initialData?.gender || "");
      setfamilycontactno(user?.relativeNumber || "");
      setlastcompany(user?.lastcompanyName || "");
      seteducation(user?.highestQualification || "");
      setpresentaddress(user?.temporaryAddress || "");
      setRelativename(user?.relativeName || "");
      setSelectedRelativeType(initialData?.relativeType || "");
      fetchSkills();
      fetchLocations();

      // Prefill referral code — ONLY for brand-new users, never on edit.
      // Reads the same source injected by the RN WebView on install (see
      // App.tsx injectReferralCode): window.__referralCode / window.referralCode,
      // falling back to localStorage. Kept as-is (alphanumeric, no digit-stripping).
      //
      // Validation: only auto-fill (and lock) the field when the captured code
      // is alphanumeric and exactly 4 or 6 characters long. Otherwise leave the
      // field blank and editable so the user can type a referrer's phone number
      // (or any other code) manually, same as before.
      if (!isExistingUser) {
        let capturedCode = window.__referralCode || window.referralCode || "";
        if (!capturedCode) {
          try {
            capturedCode = localStorage.getItem("referralCode") || "";
          } catch (e) {
            capturedCode = "";
          }
        }
        // Strip anything that isn't a letter/number
        const sanitized = capturedCode.replace(/[^a-zA-Z0-9]/g, "");

        if (isValidReferralCode(sanitized)) {
          setReferralCode(sanitized);
          setIsReferralPrefilled(true);
        } else {
          setReferralCode("");
          setIsReferralPrefilled(false);
        }
      } else {
        setReferralCode("");
        setIsReferralPrefilled(false);
      }
    }
  }, [isOpen]);

  // ── Close dropdowns on outside click ─────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (genderRef.current && !genderRef.current.contains(e.target))
        setIsGenderOpen(false);
      if (skillsRef.current && !skillsRef.current.contains(e.target))
        setIsSkillsOpen(false);
      if (locationRef.current && !locationRef.current.contains(e.target))
        setIsLocationOpen(false);
      if (relativeTypeRef.current && !relativeTypeRef.current.contains(e.target))
        setIsRelativeTypeOpen(false);
      if (educationRef.current && !educationRef.current.contains(e.target)) {
        setIsEducationOpen(false);
        setEducationSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Toggle single skill ───────────────────────────────────────────────────
  const toggleSkill = useCallback((skill) => {
  if (!skill) return;
  setSelectedSkills((prev) => {
    if (prev.some((s) => s._id === skill._id)) {
      return prev.filter((s) => s._id !== skill._id); // always allow deselect
    }
    if (prev.length >= 5) {
      toast.error("You can select a maximum of 5 skills");
      return prev;
    }
    return [...prev, skill];
  });
}, []);

  const step1Valid =
    name.trim() && selectedGender && selectedSkills.length > 0 && selectedLocation;

  const step2Valid =
    selectedRelativeType &&
    relativename.trim() &&
    familycontactno.length === 10 &&
    !phoneNumberError &&
    education.trim() &&
    presentaddress.trim();

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_URL}/lenskart/save-data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          name,
          skillIds: selectedSkills.map((s) => s._id),
          skill: selectedSkills.map((s) => s.name),
          location: selectedLocation,
          locationId: selectedlocationobject?._id,
          gender: selectedGender,
          relativeNumber: familycontactno,
          lastCompanyName: lastcompany,
          highestQualification: education,
          relativeName: relativename,
          relativeType: selectedRelativeType,
          workerId: user?.id,
          temporaryAddress: presentaddress,
          referredBy: referralCode || null,
        }),
      });

      const result = await res.json();

      if (result.isExistingUser !== undefined) {
        setUser((prev) => ({
          ...prev,
          name,
          relativeNumber: familycontactno,
          lastcompanyName: lastcompany,
          highestQualification: education,
          relativeName: relativename,
          relativeType: selectedRelativeType,
          skills: selectedSkills.map((s) => s.name),
          skillIds: selectedSkills.map((s) => s._id),
          preferredLocation: selectedLocation,
          preferredLocationId: selectedlocationobject?._id,
          gender: selectedGender,
          temporaryAddress: presentaddress,
          department: result.department || prev.department,
        }));

        toast.success(
          editType || isExistingUser
            ? "Details updated successfully!"
            : "Details saved successfully! 🎉"
        );
        await verifyToken(token);
        handleClose(!isExistingUser);
      } else {
        toast.error(result.message || "Failed to save data");
      }
    } catch {
      toast.error("Failed to save data");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (success = false) => {
    setStep(1);
    setName("");
    setSelectedSkills([]);
    setSelectedLocation("");
    setSelectedGender("");
    setselectedlocationobject(null);
    setfamilycontactno("");
    setlastcompany("");
    seteducation("");
    setpresentaddress("");
    setRelativename("");
    setSelectedRelativeType("");
    setIsSkillsOpen(false);
    setIsLocationOpen(false);
    setIsGenderOpen(false);
    setIsRelativeTypeOpen(false);
    setIsEducationOpen(false);
    setReferralCode("");
    setIsReferralPrefilled(false);
    onClose(success);
  };

  if (!isOpen) return null;

  // ── Styles ────────────────────────────────────────────────────────────────
  const inputCls =
    "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all placeholder-gray-400";
  const labelCls =
    "block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1";
  const dropdownBtnCls =
    "w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-left text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent focus:bg-white outline-none transition-all flex justify-between items-center gap-1";
  // ✅ Matches your department dropdown exactly
  const dropdownMenuCls =
    "absolute z-[9999] mt-1 w-full bg-white shadow-lg rounded-md py-1 max-h-48 overflow-auto border border-gray-300";

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 flex items-center sm:items-center justify-center p-0 sm:p-4">
      <div
        className="bg-white w-full sm:max-w-sm mx-auto flex flex-col rounded-t-2xl sm:rounded-2xl shadow-2xl border border-gray-100"
        style={{ maxHeight: "92vh" }}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-t-2xl flex-shrink-0">
          <h2 className="text-base font-bold text-white flex items-center gap-2">
            <span className="bg-white/20 rounded-lg p-1">
              <img src={worker} alt="Worker Logo" className="w-4 h-4 object-contain" />
            </span>
            {isExistingUser ? "Edit Details" : "Personal Details"}
          </h2>
          <button
            onClick={() => handleClose(false)}
            className="text-white/70 hover:text-white hover:bg-white/20 rounded-lg p-1 transition-all"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* ── Step Indicator ── */}
        <div className="flex items-center justify-center gap-3 px-4 py-2.5 border-b border-gray-100 flex-shrink-0">
          <StepDot stepNum={1} label="Basic" icon={User} currentStep={step} />
          <div className={`w-10 h-0.5 rounded transition-all duration-500 ${step > 1 ? "bg-green-400" : "bg-gray-200"}`} />
          <StepDot stepNum={2} label="Details" icon={Users} currentStep={step} />
        </div>

        {/* ── Scrollable Body ── */}
        <div className="overflow-y-auto flex-1 px-4 py-3 space-y-3">

          {/* ════ STEP 1 ════ */}
          {step === 1 && (
            <>
              {/* Name */}
              <div>
                <label className={labelCls}>
                  Full Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={inputCls}
                  placeholder="Enter your name"
                />
              </div>

              {/* Gender */}
              <div className="relative" ref={genderRef}>
                <label className={labelCls}>
                  Gender <span className="text-rose-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setIsGenderOpen((p) => !p)}
                  className={dropdownBtnCls}
                >
                  <span className={selectedGender ? "text-gray-800" : "text-gray-400"}>
                    {selectedGender || "Select gender"}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${isGenderOpen ? "rotate-180" : ""}`} />
                </button>
                {isGenderOpen && (
                  <div className={dropdownMenuCls}>
                    {["Male", "Female", "Other"].map((g) => (
                      <label
                        key={g}
                        className={`flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-indigo-100 ${
                          selectedGender === g ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                        }`}
                        onClick={() => { setSelectedGender(g); setIsGenderOpen(false); }}
                      >
                        <RadioOption selected={selectedGender === g} />
                        <span className="ml-2">{g}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Skills */}
              <div className="relative" ref={skillsRef}>
                <label className={labelCls}>
  Skills <span className="text-rose-500">*</span>{" "}
  <span className="text-gray-400 normal-case font-normal tracking-normal">
    ({selectedSkills.length > 0 ? `${selectedSkills.length}/5 selected` : ""})
  </span>
</label>
                <button
                  type="button"
                  onClick={() => setIsSkillsOpen((p) => !p)}
                  className={`${dropdownBtnCls} min-h-[38px]`}
                >
                  <span
                    className={`${
                      selectedSkills.length ? "flex flex-wrap gap-1" : "text-gray-400"
                    } flex-1 overflow-hidden`}
                  >
                    {selectedSkills.length
                      ? selectedSkills.map((s) => (
                          <span
                            key={s._id}
                            className="bg-indigo-100 text-indigo-700 text-[11px] font-medium px-1.5 py-0.5 rounded"
                          >
                            {s.name}
                          </span>
                        ))
                      : "Select skills"}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${isSkillsOpen ? "rotate-180" : ""}`} />
                </button>

                {isSkillsOpen && (
                  <div className={dropdownMenuCls}>
                    {isLoading ? (
                      <div className="px-4 py-2 text-center text-xs text-gray-400">
                        Loading skills...
                      </div>
                    ) : skills.length > 0 ? (
                      <>
                        {/* ✅ Select All — same style as your department dropdown */}
                        {skills.map((skill) => (
                          <label
                            key={skill._id}
                            className={`flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-indigo-100 ${
                              selectedSkills.some((s) => s._id === skill._id)
                                ? "bg-indigo-50 text-indigo-700"
                                : "text-gray-700"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedSkills.some((s) => s._id === skill._id)}
                              onChange={() => toggleSkill(skill)}
                              className="mr-2"
                            />
                            {skill.name}
                          </label>
                        ))}
                      </>
                    ) : (
                      <div className="px-4 py-2 text-center text-xs text-gray-400">
                        No skills available
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Location */}
              <div className="relative" ref={locationRef}>
                <label className={labelCls}>
                  Location <span className="text-rose-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setIsLocationOpen((p) => !p)}
                  className={dropdownBtnCls}
                >
                  <span className={selectedLocation ? "text-gray-800" : "text-gray-400"}>
                    {selectedLocation || "Select location"}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${isLocationOpen ? "rotate-180" : ""}`} />
                </button>

                {isLocationOpen && (
                  <div className={dropdownMenuCls}>
                    {isLoadingLocations ? (
                      <div className="px-4 py-2 text-center text-xs text-gray-400">
                        Loading locations...
                      </div>
                    ) : locations.length > 0 ? (
                      locations.map((loc) => (
                        <label
                          key={loc._id}
                          className={`flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-indigo-100 ${
                            selectedLocation === loc.name
                              ? "bg-indigo-50 text-indigo-700"
                              : "text-gray-700"
                          }`}
                          onClick={() => {
                            setSelectedLocation(loc.name);
                            setselectedlocationobject(loc);
                            setIsLocationOpen(false);
                          }}
                        >
                          {selectedLocation === loc.name && (
                            <Check className="w-3 h-3 mr-2 text-indigo-500 flex-shrink-0" />
                          )}
                          {loc.name}
                        </label>
                      ))
                    ) : (
                      <div className="px-4 py-2 text-center text-xs text-gray-400">
                        No locations available
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ════ STEP 2 ════ */}
          {step === 2 && (
            <>
              {/* Relative Type + Name */}
              <div>
                <label className={labelCls}>
                  Relative Info <span className="text-rose-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={relativename}
                    onChange={(e) => setRelativename(e.target.value)}
                    className={`${inputCls} flex-1 min-w-0`}
                    placeholder="Name"
                  />

                  <div className="relative w-[44%] flex-shrink-0" ref={relativeTypeRef}>
                    <button
                      type="button"
                      onClick={() => setIsRelativeTypeOpen((p) => !p)}
                      className={`${dropdownBtnCls} min-h-[38px]`}
                    >
                      <span className={`truncate text-xs ${selectedRelativeType ? "text-gray-800" : "text-gray-400"}`}>
                        {selectedRelativeType || "Relation with you"}
                      </span>
                      <ChevronDown className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${isRelativeTypeOpen ? "rotate-180" : ""}`} />
                    </button>

                    {isRelativeTypeOpen && (
                      <div className={dropdownMenuCls}>
                        {["Father", "Mother", "Husband", "Wife", "Brother", "Sister"].map((type) => (
                          <label
                            key={type}
                            className={`flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-indigo-100 ${
                              selectedRelativeType === type
                                ? "bg-indigo-50 text-indigo-700"
                                : "text-gray-700"
                            }`}
                            onClick={() => { setSelectedRelativeType(type); setIsRelativeTypeOpen(false); }}
                          >
                            <RadioOption selected={selectedRelativeType === type} />
                            <span className="ml-2">{type}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Family Contact */}
              <div>
  <label className={labelCls}>
    {selectedRelativeType || "Relative"} Contact No.{" "}
    <span className="text-rose-500">*</span>
  </label>
  <input
    type="text"
    value={familycontactno}
    onChange={handleFamilyContactChange}
    className={`${inputCls} ${phoneNumberError ? "border-red-500 focus:ring-red-500" : ""}`}
    placeholder="10-digit number"
    maxLength={10}
  />
  {phoneNumberError && (
    <p className="text-rose-500 text-[11px] mt-0.5">{phoneNumberError}</p>
  )}
  {!phoneNumberError && familycontactno.length > 0 && familycontactno.length < 10 && (
    <p className="text-rose-500 text-[11px] mt-0.5">Must be 10 digits</p>
  )}
</div>

              {/* Education */}
              <div className="relative" ref={educationRef}>
                <label className={labelCls}>
                  Last Education Details <span className="text-rose-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => { setIsEducationOpen((p) => !p); setEducationSearch(""); }}
                  className={`${dropdownBtnCls} min-h-[38px]`}
                >
                  <span className={`truncate text-sm ${education ? "text-gray-800" : "text-gray-400"}`}>
                    {education || "Select education level"}
                  </span>
                  <ChevronDown className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${isEducationOpen ? "rotate-180" : ""}`} />
                </button>

                {isEducationOpen && (
                  <div className="absolute z-[9999] mt-1 w-full bg-white shadow-lg rounded-md border border-gray-300 overflow-hidden">
                    {/* Search bar */}
                    <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
                      <div className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 rounded-md border border-gray-200 focus-within:border-indigo-400">
                        <Search className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <input
                          autoFocus
                          type="text"
                          value={educationSearch}
                          onChange={(e) => setEducationSearch(e.target.value)}
                          placeholder="Search education..."
                          className="flex-1 text-xs bg-transparent outline-none text-gray-700 placeholder-gray-400"
                        />
                        {educationSearch && (
                          <button onClick={() => setEducationSearch("")} className="text-gray-400 hover:text-gray-600 text-xs">
                            ✕
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="max-h-44 overflow-y-auto py-1">
                      {filteredEducation.length > 0 ? (
                        filteredEducation.map((opt) => (
                          <label
                            key={opt}
                            className={`flex items-center px-4 py-2 text-sm cursor-pointer hover:bg-indigo-100 ${
                              education === opt ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                            }`}
                            onClick={() => { seteducation(opt); setIsEducationOpen(false); setEducationSearch(""); }}
                          >
                            <RadioOption selected={education === opt} />
                            <span className="ml-2 truncate">{opt}</span>
                          </label>
                        ))
                      ) : (
                        <div className="px-4 py-5 text-center text-xs text-gray-400">
                          <div className="text-xl mb-1">🎓</div>
                          No results for "{educationSearch}"
                        </div>
                      )}
                    </div>

                    <div className="px-4 py-1.5 border-t border-gray-100 bg-gray-50">
                      <p className="text-[11px] text-gray-400">
                        {filteredEducation.length} option{filteredEducation.length !== 1 ? "s" : ""} available
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Current Address */}
              <div>
                <label className={labelCls}>
                  Current Address <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={presentaddress}
                  onChange={(e) => setpresentaddress(e.target.value)}
                  className={inputCls}
                  placeholder="Enter current address"
                />
              </div>

              {/* Last Company */}
              <div>
                <label className={labelCls}>Last Company (optional)</label>
                <input
                  type="text"
                  value={lastcompany}
                  onChange={(e) => setlastcompany(e.target.value)}
                  className={inputCls}
                  placeholder="Last company name"
                />
              </div>

              {/* Referral Code */}
              {!isExistingUser && (
  <div>
    <label className={labelCls}>Referral Code (optional)</label>
    <input
      type="text"
      value={referralCode}
      onChange={(e) => {
        if (isReferralPrefilled) return;
        const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
        if (val.length <= 20) setReferralCode(val);
      }}
      disabled={isReferralPrefilled}
      readOnly={isReferralPrefilled}
      className={`${inputCls} ${
        isReferralPrefilled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""
      }`}
      placeholder="Enter referral code"
      maxLength={20}
    />
  </div>
)}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex justify-between gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex-shrink-0">
          {step === 1 ? (
            <>
              <button
                type="button"
                onClick={() => handleClose(false)}
                className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-100 transition-all"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!step1Valid}
                onClick={() => setStep(2)}
                className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold rounded-xl shadow-sm hover:shadow-md transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-100 transition-all"
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); handleSubmit(); }}
                disabled={!step2Valid || isSubmitting}
                className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold rounded-xl shadow-sm hover:shadow-md transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting && (
                  <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                {isExistingUser ? "Update" : "Submit"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkerDetailModal;